import { useState } from 'react'
import { d165 } from '../constants/service'
import { getFinalDay, tj, getClosestPresetDate } from '../utils/date'
import { getTJPokemon } from '../utils/pokemon'
import { calculateAccumulatedMoney } from '../utils/number'

const START_DATE_KEY = 'startDate'
const DURATION_KEY = 'duration'
const IS_FEMALE_KEY = 'isFemale'
const INSIGNIA_KEY = 'insignia'
const FAVORITE_RESTAURANT_URL_KEY = 'favoriteRestaurantUrl'

export const useUserData = () => {
  const [startDate, setStartDate] = useState<Date>(() => {
    const savedStartDate = localStorage.getItem(START_DATE_KEY)
    return savedStartDate ? new Date(savedStartDate) : getClosestPresetDate()
  })

  const [duration, setDuration] = useState<number>(() => {
    const savedDuration = localStorage.getItem(DURATION_KEY)
    return savedDuration ? parseInt(savedDuration, 10) : d165
  })

  const [isFemale, setIsFemale] = useState<boolean>(() => {
    const savedIsFemale = localStorage.getItem(IS_FEMALE_KEY)
    return savedIsFemale ? savedIsFemale === 'true' : false
  })

  const [insigniaIndex, setInsigniaIndex] = useState<number>(() => {
    const savedInsignia = localStorage.getItem(INSIGNIA_KEY)
    return savedInsignia ? parseInt(savedInsignia, 10) : 0
  })

  const [favoriteRestaurantUrl, setFavoriteRestaurantUrl] = useState<string | null>(() => {
    const savedUrl = localStorage.getItem(FAVORITE_RESTAURANT_URL_KEY)
    return savedUrl ?? null
  })

  const saveStartDate = (date: Date | null) => {
    if (!date) {
      return
    }

    const formatted = startDate.toISOString().split('T')[0]
    localStorage.setItem(START_DATE_KEY, formatted)
    setStartDate(date)
  }

  const saveDuration = (duration: number | null) => {
    if (!duration || duration < 0 || duration > 400) {
      localStorage.setItem(DURATION_KEY, '0')
      setDuration(0)
      return
    }

    localStorage.setItem(DURATION_KEY, duration.toString())
    setDuration(duration)
  }

  const saveIsFemale = (value: boolean) => {
    localStorage.setItem(IS_FEMALE_KEY, value.toString())
    setIsFemale(value)
  }

  const saveInsignia = (index: number) => {
    if (index < 0) {
      return
    }

    localStorage.setItem(INSIGNIA_KEY, index.toString())
    setInsigniaIndex(index)
  }

  const saveFavoriteRestaurantUrl = (url: string | null) => {
    if (!url) {
      localStorage.removeItem(FAVORITE_RESTAURANT_URL_KEY)
      setFavoriteRestaurantUrl(null)
      return
    }

    localStorage.setItem(FAVORITE_RESTAURANT_URL_KEY, url)
    setFavoriteRestaurantUrl(url)
  }

  const daysRemaining = tj(startDate, duration)

  const serviceDays = duration - daysRemaining

  const percentageCompleted = Math.round((serviceDays / duration) * 100)

  const pokemon = getTJPokemon(daysRemaining)

  const accumulatedMoney = calculateAccumulatedMoney(serviceDays, isFemale)

  const finalDate = getFinalDay(startDate, duration)

  return {
    startDate,
    duration,
    saveStartDate,
    saveDuration,
    isFemale,
    saveIsFemale,
    insigniaIndex,
    saveInsignia,
    favoriteRestaurantUrl,
    saveFavoriteRestaurantUrl,
    daysRemaining,
    serviceDays,
    percentageCompleted,
    pokemon,
    accumulatedMoney,
    finalDate,
  }
}
